import { forwardRef } from 'react';
import { cn } from '@/lib/cn';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  hint?: string;
  icon?: React.ReactNode;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, hint, icon, className, id, ...props }, ref) => {
    const inputId = id || label?.toLowerCase().replace(/\s+/g, '-');

    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={inputId}
            className="block text-xs font-medium text-slate-600 mb-1.5"
          >
            {label}
          </label>
        )}

        <div className="relative">
          {icon && (
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 [&>svg]:w-4 [&>svg]:h-4">
              {icon}
            </div>
          )}

          <input
            ref={ref}
            id={inputId}
            className={cn(
              'w-full h-9 rounded-lg border bg-white px-3 text-sm text-slate-900',
              'placeholder:text-slate-400',
              'transition-colors duration-150',
              'focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-400',
              icon && 'pl-9',
              error
                ? 'border-red-300 focus:ring-red-500/20 focus:border-red-400'
                : 'border-slate-200 hover:border-slate-300',
              'disabled:opacity-50 disabled:bg-slate-50',
              className,
            )}
            {...props}
          />
        </div>

        {(error || hint) && (
          <p
            className={cn(
              'text-xs mt-1',
              error ? 'text-red-500' : 'text-slate-400',
            )}
          >
            {error || hint}
          </p>
        )}
      </div>
    );
  },
);

Input.displayName = 'Input';
